'use client';

import { ChevronDownIcon, MapPinIcon } from '@/components/ui/icons';
import { cn } from '@/lib/ui/cn';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { useRef, useState } from 'react';

export type RegionSelectOptions = { value: string; label: string }[];

interface RegionSelectProps {
  id: string;
  label: string;
  options: RegionSelectOptions;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function RegionSelect({
  id,
  label,
  options,
  value,
  onChange,
  className,
}: RegionSelectProps) {
  const usedKeyboard = useRef<boolean | null>(null);
  const [showRing, setShowRing] = useState(false);
  const labelId = `${id}-label`;
  const selected = options.find(option => option.value === value);

  return (
    <div
      data-component='region-select'
      data-testid='region-select'
      className={cn('w-full max-w-[841px]', className)}
    >
      <span id={labelId} className='sh-small mb-1.5 block font-medium'>
        {label}
      </span>

      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger
          id={id}
          aria-labelledby={`${labelId} ${id}`}
          data-testid='region-select-trigger'
          onFocus={event =>
            setShowRing(
              usedKeyboard.current ?? event.target.matches(':focus-visible')
            )
          }
          onBlur={() => {
            setShowRing(false);
            usedKeyboard.current = null;
          }}
          className={cn(
            'group border-sh-bronze flex h-14 w-full cursor-pointer items-center justify-between gap-3 rounded-[40px] border-2 bg-white px-4 text-left outline-none',
            showRing && 'shadow-[0_0_0_2px_var(--color-sh-bronze)]'
          )}
        >
          <span className='flex min-w-0 items-center gap-3'>
            <MapPinIcon className='text-sh-black size-5 shrink-0' />
            <span className='sh-body text-sh-black truncate font-medium'>
              {selected?.label}
            </span>
          </span>
          <ChevronDownIcon className='text-sh-black w-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180' />
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align='start'
            sideOffset={8}
            onKeyDown={() => {
              usedKeyboard.current = true;
            }}
            onPointerDown={() => {
              usedKeyboard.current = false;
            }}
            onPointerDownOutside={() => {
              usedKeyboard.current = false;
            }}
            data-testid='region-select-list'
            className='border-sh-bronze z-50 w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden rounded-[40px] border-2 bg-white py-1 shadow-lg'
          >
            <DropdownMenu.RadioGroup value={value} onValueChange={onChange}>
              {options.map(option => (
                <DropdownMenu.RadioItem
                  key={option.value}
                  value={option.value}
                  className='sh-body text-sh-black data-[state=checked]:bg-sh-cream data-highlighted:bg-sh-paper flex cursor-pointer items-center justify-between gap-3 px-5 py-3 outline-none data-[state=checked]:font-medium'
                >
                  <span className='truncate'>{option.label}</span>
                  <DropdownMenu.ItemIndicator className='shrink-0'>
                    <Check className='size-5' aria-hidden='true' />
                  </DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
